/**
 * API service layer using Axios.
 * Centralizes all HTTP requests to the backend API.
 */

import axios, { AxiosError, AxiosInstance } from 'axios';
import type {
  UploadResponse,
  Transaction,
  TransactionListResponse,
  VlanQueryResponse,
  VlanListResponse,
  InterfaceQueryResponse,
  InterfaceListResponse,
  SwitchQueryResponse,
  SwitchListResponse,
  ReachabilityRequest,
  ReachabilityResponse,
  SearchResponse,
  SwitchVersionsResponse,
  ComparisonRequest,
  ComparisonResult,
} from '@/types/entities';
import type {
  LoginRequest,
  LoginResponse,
  PasswordChangeRequest,
  MessageResponse,
  User,
} from '@/types/auth';
import type {
  UserListResponse,
  UserCreateRequest,
  UserUpdateRequest,
  PasswordResetRequest,
} from '@/types/admin';
import type { Role } from '@/types/auth';

// Create Axios instance with base configuration
// Use nullish coalescing (??) to allow empty string for relative URLs
const baseURL = import.meta.env.VITE_API_BASE_URL ?? '';
console.log('🔍 VITE_API_BASE_URL:', import.meta.env.VITE_API_BASE_URL);
console.log('🔍 Using baseURL:', baseURL);

const apiClient: AxiosInstance = axios.create({
  baseURL: baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies with requests
});

// Request interceptor (cookies are automatically included via withCredentials)
apiClient.interceptors.request.use(
  (config) => {
    // Session authentication uses httpOnly cookies, which are automatically
    // included in requests when withCredentials is true
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      // Server responded with error status
      console.error('API Error:', error.response.status, error.response.data);
    } else if (error.request) {
      // Request made but no response
      console.error('Network Error:', error.message);
    } else {
      // Something else happened
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

/**
 * API service object with all endpoint methods
 */
export const api = {
  // Health check
  health: async (): Promise<{ status: string; service: string; neo4j: string }> => {
    const response = await apiClient.get('/health');
    return response.data;
  },

  // Authentication endpoints
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/login', credentials);
    return response.data;
  },

  logout: async (): Promise<MessageResponse> => {
    const response = await apiClient.post<MessageResponse>('/auth/logout');
    return response.data;
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<User>('/auth/me');
    return response.data;
  },

  changePassword: async (passwordRequest: PasswordChangeRequest): Promise<MessageResponse> => {
    const response = await apiClient.post<MessageResponse>('/auth/password', passwordRequest);
    return response.data;
  },

  // Admin user management endpoints (admin-only)
  admin: {
    // List all users
    listUsers: async (): Promise<UserListResponse> => {
      const response = await apiClient.get<UserListResponse>('/admin/users');
      return response.data;
    },

    // Create new user
    createUser: async (userData: UserCreateRequest): Promise<User> => {
      const response = await apiClient.post<User>('/admin/users', userData);
      return response.data;
    },

    // Update existing user
    updateUser: async (userId: number, userData: UserUpdateRequest): Promise<User> => {
      const response = await apiClient.patch<User>(`/admin/users/${userId}`, userData);
      return response.data;
    },

    // Reset user password
    resetPassword: async (userId: number, resetData: PasswordResetRequest): Promise<MessageResponse> => {
      const response = await apiClient.post<MessageResponse>(
        `/admin/users/${userId}/reset-password`,
        resetData
      );
      return response.data;
    },

    // Deactivate user (soft delete)
    deactivateUser: async (userId: number): Promise<MessageResponse> => {
      const response = await apiClient.delete<MessageResponse>(`/admin/users/${userId}`);
      return response.data;
    },

    // List all roles
    listRoles: async (): Promise<Role[]> => {
      const response = await apiClient.get<Role[]>('/admin/roles');
      return response.data;
    },
  },

  // Upload endpoints
  upload: async (
    file: File,
    username: string,
    onProgress?: (progress: number) => void
  ): Promise<UploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    interface BackendUploadResponse {
      transaction_id: string;
      filename: string;
      file_size: number;
      upload_timestamp: string;
      file_hash: string;
      processing_status?: 'success' | 'failed' | 'warning';
      processing_message?: string;
      error_details?: string[];
      entities_created?: {
        switches?: number;
        interfaces?: number;
        vlan_configs?: number;
        canonical_vlans?: number;
        relationships?: number;
      };
    }

    try {
      const response = await apiClient.post<BackendUploadResponse>('/upload/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'x-uploader-username': username,
        },
        onUploadProgress: (event) => {
          if (onProgress && event.total) {
            const percentComplete = Math.round((event.loaded * 100) / event.total);
            onProgress(percentComplete);
          }
        },
      });

      // Transform snake_case response to camelCase
      return {
        transactionId: response.data.transaction_id,
        filename: response.data.filename,
        fileSize: response.data.file_size,
        uploadTimestamp: response.data.upload_timestamp,
        fileHash: response.data.file_hash,
        processingStatus: response.data.processing_status,
        processingMessage: response.data.processing_message,
        errorDetails: response.data.error_details,
        entitiesCreated: response.data.entities_created,
      };
    } catch (error) {
      // Handle HTTP 400 validation errors with detailed messages
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        const detail = error.response.data?.detail;
        if (detail && typeof detail === 'object' && 'message' in detail) {
          // This is a file validation error with detailed structure
          throw new Error(detail.message as string);
        } else if (typeof detail === 'string') {
          throw new Error(detail);
        }
      }
      // Re-throw the error for other cases
      throw error;
    }
  },

  // Transaction endpoints
  getTransactions: async (params?: {
    limit?: number;
    offset?: number;
    uploader?: string;
    startDate?: string;
    endDate?: string;
    orderBy?: string;
    order?: 'asc' | 'desc';
  }): Promise<TransactionListResponse> => {
    interface BackendTransaction {
      transaction_id: string;
      filename: string;
      file_size: number;
      file_hash: string;
      upload_timestamp: string;
      uploader_username: string;
      storage_path: string;
      status: string;
    }

    interface BackendResponse {
      transactions: BackendTransaction[];
      total: number;
      limit: number;
      offset: number;
    }

    // Transform frontend params to backend format
    const backendParams: {
      limit?: number;
      offset?: number;
      uploader?: string;
      start_date?: string;
      end_date?: string;
      order_by?: string;
      order?: 'asc' | 'desc';
    } = {};
    if (params?.limit !== undefined) backendParams.limit = params.limit;
    if (params?.offset !== undefined) backendParams.offset = params.offset;
    if (params?.uploader) backendParams.uploader = params.uploader;
    if (params?.startDate) backendParams.start_date = params.startDate;
    if (params?.endDate) backendParams.end_date = params.endDate;
    if (params?.orderBy) backendParams.order_by = params.orderBy;
    if (params?.order) backendParams.order = params.order;

    const response = await apiClient.get<BackendResponse>('/transactions/', {
      params: backendParams,
    });

    // Transform snake_case response to camelCase
    return {
      transactions: response.data.transactions.map((t) => ({
        transactionId: t.transaction_id,
        filename: t.filename,
        fileSize: t.file_size,
        fileHash: t.file_hash,
        uploadTimestamp: t.upload_timestamp,
        uploaderUsername: t.uploader_username,
        storagePath: t.storage_path,
        status: t.status as 'completed' | 'processing' | 'failed',
      })),
      total: response.data.total,
      limit: response.data.limit,
      offset: response.data.offset,
    };
  },

  getTransaction: async (transactionId: string): Promise<Transaction> => {
    const response = await apiClient.get(`/transactions/${transactionId}`);
    return response.data;
  },

  downloadTransactionFile: async (transactionId: string): Promise<Blob> => {
    const response = await apiClient.get(`/transactions/${transactionId}/file`, {
      responseType: 'blob',
    });
    return response.data;
  },

  deleteTransaction: async (transactionId: string, deleterUsername?: string): Promise<Transaction> => {
    interface BackendTransaction {
      transaction_id: string;
      filename: string;
      file_size: number;
      file_hash: string;
      upload_timestamp: string;
      uploader_username: string;
      storage_path: string;
      status: string;
      deleted_at?: string | null;
      deleted_by?: string | null;
    }

    const headers: Record<string, string> = {};
    if (deleterUsername) {
      headers['X-Deleter-Username'] = deleterUsername;
    }

    const response = await apiClient.delete<BackendTransaction>(`/transactions/${transactionId}`, {
      headers,
    });

    // Transform snake_case response to camelCase
    return {
      transactionId: response.data.transaction_id,
      filename: response.data.filename,
      fileSize: response.data.file_size,
      fileHash: response.data.file_hash,
      uploadTimestamp: response.data.upload_timestamp,
      uploaderUsername: response.data.uploader_username,
      storagePath: response.data.storage_path,
      status: response.data.status as 'completed' | 'processing' | 'failed' | 'deleted',
      deletedAt: response.data.deleted_at || undefined,
      deletedBy: response.data.deleted_by || undefined,
    };
  },

  emptyTrash: async (): Promise<{
    deletedCount: number;
    deletedTransactions: string[];
    totalSizeFreed: number;
    errors: Array<{ file: string; error: string }>;
  }> => {
    interface BackendResponse {
      deleted_count: number;
      deleted_transactions: string[];
      total_size_freed: number;
      errors: Array<{ file: string; error: string }>;
    }

    const response = await apiClient.post<BackendResponse>('/transactions/empty-trash');

    return {
      deletedCount: response.data.deleted_count,
      deletedTransactions: response.data.deleted_transactions,
      totalSizeFreed: response.data.total_size_freed,
      errors: response.data.errors,
    };
  },

  // Graph query endpoints
  getVlan: async (vlanId: number): Promise<VlanQueryResponse> => {
    const response = await apiClient.get(`/api/graph/vlan/${vlanId}`);
    return response.data;
  },

  getVlans: async (): Promise<VlanListResponse> => {
    const response = await apiClient.get('/api/graph/vlans');
    return response.data;
  },

  getInterface: async (uniqueId: string): Promise<InterfaceQueryResponse> => {
    const response = await apiClient.get(`/api/graph/interface/${uniqueId}`);
    return response.data;
  },

  getInterfaces: async (): Promise<InterfaceListResponse> => {
    const response = await apiClient.get('/api/graph/interfaces');
    return response.data;
  },

  getSwitch: async (hostname: string): Promise<SwitchQueryResponse> => {
    const response = await apiClient.get(`/api/graph/switch/${hostname}`);
    return response.data;
  },

  getSwitches: async (): Promise<SwitchListResponse> => {
    const response = await apiClient.get('/api/graph/switches');
    return response.data;
  },

  checkReachability: async (request: ReachabilityRequest): Promise<ReachabilityResponse> => {
    const response = await apiClient.post('/api/graph/reachability', null, {
      params: {
        interface1: request.interface1,
        interface2: request.interface2,
      },
    });

    // Transform snake_case response to camelCase
    return {
      reachable: response.data.reachable,
      sharedVlans: response.data.shared_vlans || [],
      path: response.data.path,
    };
  },

  // Search endpoint (FRONTEND-013)
  search: async (
    query: string,
    type?: 'switch' | 'vlan' | 'interface',
    limit?: number
  ): Promise<SearchResponse> => {
    const params: {
      q: string;
      type?: string;
      limit?: number;
    } = { q: query };

    if (type) params.type = type;
    if (limit) params.limit = limit;

    const response = await apiClient.get<SearchResponse>('/api/search', { params });
    return response.data;
  },

  // Switch history deletion endpoint
  deleteSwitchHistory: async (
    hostname: string,
    keepVersions: number = 1
  ): Promise<{
    switchesDeleted: number;
    interfacesDeleted: number;
    totalVersions: number;
    versionsKept: number;
    message: string;
  }> => {
    interface BackendResponse {
      switches_deleted: number;
      interfaces_deleted: number;
      total_versions: number;
      versions_kept: number;
      message: string;
    }

    const response = await apiClient.delete<BackendResponse>(
      `/api/graph/switch/${encodeURIComponent(hostname)}/history`,
      {
        params: {
          keep_versions: keepVersions,
        },
      }
    );

    return {
      switchesDeleted: response.data.switches_deleted,
      interfacesDeleted: response.data.interfaces_deleted,
      totalVersions: response.data.total_versions,
      versionsKept: response.data.versions_kept,
      message: response.data.message,
    };
  },

  // Get switch version history (COMP-005)
  getSwitchVersions: async (hostname: string): Promise<SwitchVersionsResponse> => {
    interface BackendVersionSummary {
      transaction_id: string;
      upload_timestamp: string;
      uploader_username: string;
      filename: string;
      file_size: number;
      valid_from: string;
      valid_to?: string | null;
      interface_count: number;
      vlan_count: number;
      changes_from_previous?: {
        interfaces_added: number;
        interfaces_removed: number;
        interfaces_modified: number;
        vlans_added: number;
        vlans_removed: number;
        vlans_modified: number;
      };
    }

    interface BackendResponse {
      hostname: string;
      versions: BackendVersionSummary[];
      current_version: string | null;
      total_versions: number;
    }

    const response = await apiClient.get<BackendResponse>(
      `/api/graph/switch/${encodeURIComponent(hostname)}/versions`
    );

    // Transform snake_case response to camelCase
    return {
      hostname: response.data.hostname,
      versions: response.data.versions.map((v) => ({
        transactionId: v.transaction_id,
        uploadTimestamp: v.upload_timestamp,
        uploaderUsername: v.uploader_username,
        filename: v.filename,
        fileSize: v.file_size,
        validFrom: v.valid_from,
        validTo: v.valid_to,
        interfaceCount: v.interface_count,
        vlanCount: v.vlan_count,
        changesFromPrevious: v.changes_from_previous
          ? {
              interfacesAdded: v.changes_from_previous.interfaces_added,
              interfacesRemoved: v.changes_from_previous.interfaces_removed,
              interfacesModified: v.changes_from_previous.interfaces_modified,
              vlansAdded: v.changes_from_previous.vlans_added,
              vlansRemoved: v.changes_from_previous.vlans_removed,
              vlansModified: v.changes_from_previous.vlans_modified,
            }
          : undefined,
      })),
      currentVersion: response.data.current_version,
      totalVersions: response.data.total_versions,
    };
  },

  // Get switch configuration at specific historical transaction (COMP-004)
  getSwitchAtVersion: async (
    hostname: string,
    transactionId: string
  ): Promise<SwitchQueryResponse> => {
    const response = await apiClient.get<SwitchQueryResponse>(
      `/api/graph/switch/${encodeURIComponent(hostname)}/version/${encodeURIComponent(transactionId)}`
    );
    return response.data;
  },

  // Delete specific switch versions by transaction ID
  deleteSwitchVersions: async (
    hostname: string,
    transactionIds: string[]
  ): Promise<{
    switchesDeleted: number;
    interfacesDeleted: number;
    transactionIdsDeleted: string[];
    message: string;
  }> => {
    interface BackendResponse {
      switches_deleted: number;
      interfaces_deleted: number;
      transaction_ids_deleted: string[];
      message: string;
    }

    const response = await apiClient.post<BackendResponse>(
      `/api/graph/switch/${encodeURIComponent(hostname)}/versions/delete`,
      {
        transaction_ids: transactionIds,
      }
    );

    return {
      switchesDeleted: response.data.switches_deleted,
      interfacesDeleted: response.data.interfaces_deleted,
      transactionIdsDeleted: response.data.transaction_ids_deleted,
      message: response.data.message,
    };
  },

  /**
   * Completely retire a switch by deleting ALL versions and configurations.
   * This is used when a switch has been permanently decommissioned.
   * ⚠️ WARNING: This action is irreversible!
   */
  retireSwitch: async (
    hostname: string
  ): Promise<{
    switchesDeleted: number;
    interfacesDeleted: number;
    message: string;
  }> => {
    interface BackendResponse {
      switches_deleted: number;
      interfaces_deleted: number;
      message: string;
    }

    const response = await apiClient.delete<BackendResponse>(
      `/api/graph/switch/${encodeURIComponent(hostname)}`
    );

    return {
      switchesDeleted: response.data.switches_deleted,
      interfacesDeleted: response.data.interfaces_deleted,
      message: response.data.message,
    };
  },

  // Compare two switch configuration versions (COMP-013)
  compareVersions: async (request: ComparisonRequest): Promise<ComparisonResult> => {
    interface BackendComparisonRequest {
      hostname: string;
      transaction_id_a: string;
      transaction_id_b: string;
      compare_types?: string[];
    }

    interface BackendPropertyChange {
      property_name: string;
      before_value: any;
      after_value: any;
    }

    interface BackendInterfaceAdded {
      unique_id: string;
      name: string;
      mode?: string;
      status?: string;
      vlan?: number;
      vlans?: number[];
      description?: string;
    }

    interface BackendInterfaceChange {
      unique_id: string;
      name: string;
      changes: BackendPropertyChange[];
    }

    interface BackendInterfaceChanges {
      added: BackendInterfaceAdded[];
      removed: BackendInterfaceAdded[];
      modified: BackendInterfaceChange[];
      unchanged: string[];
    }

    interface BackendVlanAdded {
      vlan_id: number;
      switch_hostname: string;
      name?: string;
      status?: string;
      access_interfaces: string[];
      trunk_interfaces: string[];
    }

    interface BackendVlanChange {
      vlan_id: number;
      switch_hostname: string;
      name?: string;
      status?: string;
      changes: BackendPropertyChange[];
      access_interfaces: string[];
      trunk_interfaces: string[];
      interfaces_added: string[];
      interfaces_removed: string[];
    }

    interface BackendVlanChanges {
      added: BackendVlanAdded[];
      removed: BackendVlanAdded[];
      modified: BackendVlanChange[];
      unchanged: number[];
    }

    interface BackendChangeSummary {
      interfaces_added: number;
      interfaces_removed: number;
      interfaces_modified: number;
      vlans_added: number;
      vlans_removed: number;
      vlans_modified: number;
    }

    interface BackendVersionSummary {
      transaction_id: string;
      upload_timestamp: string;
      uploader_username: string;
      filename: string;
      file_size: number;
      valid_from: string;
      valid_to?: string | null;
      interface_count: number;
      vlan_count: number;
      changes_from_previous?: BackendChangeSummary;
    }

    interface BackendComparisonResult {
      hostname: string;
      version_a: BackendVersionSummary;
      version_b: BackendVersionSummary;
      summary: BackendChangeSummary;
      interface_changes: BackendInterfaceChanges;
      vlan_changes: BackendVlanChanges;
    }

    // Transform request to backend format
    const backendRequest: BackendComparisonRequest = {
      hostname: request.hostname,
      transaction_id_a: request.transactionIdA,
      transaction_id_b: request.transactionIdB,
      compare_types: request.compareTypes,
    };

    const response = await apiClient.post<BackendComparisonResult>(
      '/api/graph/compare',
      backendRequest
    );

    // Transform response to frontend format (camelCase)
    return {
      hostname: response.data.hostname,
      versionA: {
        transactionId: response.data.version_a.transaction_id,
        uploadTimestamp: response.data.version_a.upload_timestamp,
        uploaderUsername: response.data.version_a.uploader_username,
        filename: response.data.version_a.filename,
        fileSize: response.data.version_a.file_size,
        validFrom: response.data.version_a.valid_from,
        validTo: response.data.version_a.valid_to,
        interfaceCount: response.data.version_a.interface_count,
        vlanCount: response.data.version_a.vlan_count,
        changesFromPrevious: response.data.version_a.changes_from_previous
          ? {
              interfacesAdded: response.data.version_a.changes_from_previous.interfaces_added,
              interfacesRemoved: response.data.version_a.changes_from_previous.interfaces_removed,
              interfacesModified: response.data.version_a.changes_from_previous.interfaces_modified,
              vlansAdded: response.data.version_a.changes_from_previous.vlans_added,
              vlansRemoved: response.data.version_a.changes_from_previous.vlans_removed,
              vlansModified: response.data.version_a.changes_from_previous.vlans_modified,
            }
          : undefined,
      },
      versionB: {
        transactionId: response.data.version_b.transaction_id,
        uploadTimestamp: response.data.version_b.upload_timestamp,
        uploaderUsername: response.data.version_b.uploader_username,
        filename: response.data.version_b.filename,
        fileSize: response.data.version_b.file_size,
        validFrom: response.data.version_b.valid_from,
        validTo: response.data.version_b.valid_to,
        interfaceCount: response.data.version_b.interface_count,
        vlanCount: response.data.version_b.vlan_count,
        changesFromPrevious: response.data.version_b.changes_from_previous
          ? {
              interfacesAdded: response.data.version_b.changes_from_previous.interfaces_added,
              interfacesRemoved: response.data.version_b.changes_from_previous.interfaces_removed,
              interfacesModified: response.data.version_b.changes_from_previous.interfaces_modified,
              vlansAdded: response.data.version_b.changes_from_previous.vlans_added,
              vlansRemoved: response.data.version_b.changes_from_previous.vlans_removed,
              vlansModified: response.data.version_b.changes_from_previous.vlans_modified,
            }
          : undefined,
      },
      summary: {
        interfacesAdded: response.data.summary.interfaces_added,
        interfacesRemoved: response.data.summary.interfaces_removed,
        interfacesModified: response.data.summary.interfaces_modified,
        vlansAdded: response.data.summary.vlans_added,
        vlansRemoved: response.data.summary.vlans_removed,
        vlansModified: response.data.summary.vlans_modified,
      },
      interfaceChanges: {
        added: response.data.interface_changes.added.map((i) => ({
          uniqueId: i.unique_id,
          name: i.name,
          mode: i.mode,
          status: i.status,
          vlan: i.vlan,
          vlans: i.vlans,
          description: i.description,
        })),
        removed: response.data.interface_changes.removed.map((i) => ({
          uniqueId: i.unique_id,
          name: i.name,
          mode: i.mode,
          status: i.status,
          vlan: i.vlan,
          vlans: i.vlans,
          description: i.description,
        })),
        modified: response.data.interface_changes.modified.map((i) => ({
          uniqueId: i.unique_id,
          name: i.name,
          changes: i.changes.map((c) => ({
            propertyName: c.property_name,
            beforeValue: c.before_value,
            afterValue: c.after_value,
          })),
        })),
        unchanged: response.data.interface_changes.unchanged,
      },
      vlanChanges: {
        added: response.data.vlan_changes.added.map((v) => ({
          vlanId: v.vlan_id,
          switchHostname: v.switch_hostname,
          name: v.name,
          status: v.status,
          access_interfaces: v.access_interfaces,
          trunk_interfaces: v.trunk_interfaces,
        })),
        removed: response.data.vlan_changes.removed.map((v) => ({
          vlanId: v.vlan_id,
          switchHostname: v.switch_hostname,
          name: v.name,
          status: v.status,
          access_interfaces: v.access_interfaces,
          trunk_interfaces: v.trunk_interfaces,
        })),
        modified: response.data.vlan_changes.modified.map((v) => ({
          vlanId: v.vlan_id,
          switchHostname: v.switch_hostname,
          name: v.name,
          status: v.status,
          changes: v.changes.map((c) => ({
            propertyName: c.property_name,
            beforeValue: c.before_value,
            afterValue: c.after_value,
          })),
          access_interfaces: v.access_interfaces,
          trunk_interfaces: v.trunk_interfaces,
          interfaces_added: v.interfaces_added,
          interfaces_removed: v.interfaces_removed,
        })),
        unchanged: response.data.vlan_changes.unchanged,
      },
    };
  },
};

export default api;
